import type { Witness } from '../store/booksStore'
import type { AnchoredRow } from '../types/anchoredRowTypes'
import type { ChapterCharacterLayer } from '../types/characterLayerTypes'
import type { LedgerSafeEditorialEvent } from '../types/ledgerSafeEditorialEvents'
import type { LedgerSafeLlmContributor } from '../types/ledgerSafeProvenance'
import type {
  LedgerSafeAgentAtomicMutation,
  LedgerSafeAgentChangeSetRecord,
  LedgerSafeAgentRecoveryState,
  LedgerSafeAuthorityHeadRecord,
} from '../types/ledgerSafeAgentChangeSets'
import type { PiMatrixAgentPatch } from '../types/piMatrixAgent'
import { applyLedgerSafeMergeUnitsToSegmentation } from './ledgerSafeBoundaryMergeApply'
import { reconcileLedgerSafeBoundaryEdit } from './ledgerSafeBoundaryReconcile'
import { applyLedgerSafeBoundaryShiftToSegmentation } from './ledgerSafeBoundaryRowUpdates'
import { applyLedgerSafeUnitSplitToSegmentation } from './ledgerSafeBoundarySplitApply'
import { rebaseCharacterLayerAfterSegmentationEdit } from './characterLayerSegmentationRebase'
import {
  applyLedgerSafeClearGap,
  applyLedgerSafeMarkGap,
  applyLedgerSafeShiftRange,
} from './ledgerSafeRowEditing'
import {
  buildLedgerSafeEditorialRowSnapshots,
  createLedgerSafeEditorialEvent,
} from './ledgerSafeEditorialEventStore'
import {
  buildPiMatrixAgentCharacterIndex,
  buildPiMatrixAgentSnapshot,
} from './piMatrixAgentSnapshot'
import { dbStorage } from './indexedDBStorage'

const clone = <T,>(value: T): T => structuredClone(value)
const unique = (values: string[]): string[] => Array.from(new Set(values.filter(Boolean)))
const nowIso = (): string => new Date().toISOString()

const newAuthorityRevisionId = (): string => {
  const suffix = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`
  return `authority-revision-${suffix}`
}

const operationWitnessId = (operation: PiMatrixAgentPatch['operations'][number]): string =>
  String(operation.payload.witnessId || operation.targetWitnessIds[0] || '').trim()

const operationRowIds = (operation: PiMatrixAgentPatch['operations'][number]): string[] => {
  const payloadRows = operation.payload.rowIds
  return Array.isArray(payloadRows)
    ? unique(payloadRows.map(value => String(value)))
    : unique(operation.targetRowIds.map(value => String(value)))
}

const updateWitnessSegmentation = (
  witness: Witness,
  segmentationResult: NonNullable<Witness['segmentation_result']>,
  timestamp: number,
): Witness => {
  let segmentationRuns = witness.segmentation_runs
  const activeRunId = witness.active_segmentation_run
  if (activeRunId && segmentationRuns?.[activeRunId]) {
    const activeRun = segmentationRuns[activeRunId]
    const chunks = Array.isArray(segmentationResult.chunk_results) ? segmentationResult.chunk_results : []
    segmentationRuns = {
      ...segmentationRuns,
      [activeRunId]: {
        ...activeRun,
        result: segmentationResult,
        totalChunks: chunks.length,
        totalUnits: chunks.reduce(
          (sum, chunk) => sum + (Array.isArray(chunk.micro_units) ? chunk.micro_units.length : 0),
          0,
        ),
      },
    }
  }
  return {
    ...witness,
    segmentation_result: segmentationResult,
    segmentation_runs: segmentationRuns,
    last_modified: timestamp,
    version: (witness.version || 0) + 1,
  }
}

const selectRows = (rows: AnchoredRow[], rowIds: string[]): AnchoredRow[] => {
  const selected = new Set(rowIds)
  return rows.filter(row => selected.has(row.id)).map(clone)
}

const selectWitnesses = (witnesses: Witness[], witnessIds: string[]): Witness[] => {
  const selected = new Set(witnessIds)
  return witnesses.filter(witness => selected.has(witness.id)).map(clone)
}

const validateSequence = (
  record: LedgerSafeAgentChangeSetRecord,
  patch: PiMatrixAgentPatch,
): void => {
  if (record.status !== 'awaiting_approval') {
    throw new Error(`Agent change set ${record.id} is ${record.status}, not awaiting approval.`)
  }
  if (patch.changeSetId !== record.changeSetId || patch.changeSetId !== record.id) {
    throw new Error('Prepared patch does not belong to the stored agent change set.')
  }
  if (patch.baseSnapshotId !== record.baseAuthorityRevisionId) {
    throw new Error('Prepared patch does not target the stored authority revision.')
  }
  if (patch.operations.length !== record.steps.length || patch.operations.length === 0) {
    throw new Error('Prepared patch does not contain the complete ordered edit sequence.')
  }
  if (
    !patch.invariants.sourceTextPreserved
    || !patch.invariants.changedWitnessesScoped
    || !patch.invariants.changedRowsScoped
    || patch.invariants.violations.length > 0
  ) {
    throw new Error('Prepared patch failed its edit-sequence invariants.')
  }
}

/** Build the exact ordered patch from the durable review record.
 *
 * The record intentionally carries the complete operations so a scholar can
 * approve after a browser refresh or sidecar restart. This mirrors the sidecar
 * `createPatchFromChangeSet` contract; the application path then revalidates
 * the sequence and all authority/state preconditions atomically.
 */
export const buildLedgerSafeAgentPatch = (
  record: LedgerSafeAgentChangeSetRecord,
): PiMatrixAgentPatch => ({
  patchId: `patch:${record.changeSetId}`,
  changeSetId: record.changeSetId,
  baseSnapshotId: record.baseSnapshotId,
  finalDraftSnapshotId: record.finalDraftSnapshotId,
  operations: record.steps.map(step => ({
    kind: step.operation.kind,
    targetWitnessIds: [...step.affectedWitnessIds],
    targetRowIds: [...step.affectedRowIds],
    payload: Object.fromEntries(Object.entries(step.operation).filter(([key]) => key !== 'kind')),
  })),
  invariants: clone(record.invariants),
})

export interface PrepareLedgerSafeAgentApplicationInput {
  record: LedgerSafeAgentChangeSetRecord
  patch: PiMatrixAgentPatch
  chapterLabel: string
  rows: AnchoredRow[]
  witnesses: Witness[]
  characterLayer?: ChapterCharacterLayer
  persistCharacterLayer?: boolean
  includeCharacterIndex?: boolean
  model: Pick<LedgerSafeLlmContributor, 'provider' | 'modelId' | 'displayName' | 'thinkingLevel'>
  uiContext: LedgerSafeEditorialEvent['uiContext']
  approvedAt?: string
}

export interface PreparedLedgerSafeAgentApplication {
  mutation: LedgerSafeAgentAtomicMutation
  rows: AnchoredRow[]
  witnesses: Witness[]
  characterLayer?: ChapterCharacterLayer
}

export const prepareLedgerSafeAgentApplication = (
  input: PrepareLedgerSafeAgentApplicationInput,
): PreparedLedgerSafeAgentApplication => {
  validateSequence(input.record, input.patch)
  let rows = clone(input.rows)
  const originalRows = clone(input.rows)
  const witnessById = new Map(input.witnesses.map(witness => [witness.id, clone(witness)]))
  const originalWitnesses = clone(input.witnesses)
  const changedRowIds = new Set<string>()
  const changedWitnessIds = new Set<string>()
  let characterLayer = input.characterLayer ? clone(input.characterLayer) : undefined
  const persistCharacterLayer = input.persistCharacterLayer ?? Boolean(input.characterLayer)
  const originalCharacterLayer = persistCharacterLayer && input.characterLayer
    ? clone(input.characterLayer)
    : undefined
  const timestamp = Date.now()

  input.patch.operations.forEach((operation, operationIndex) => {
    const witnessId = operationWitnessId(operation)
    if (!witnessId) {
      throw new Error(`Agent edit ${operationIndex + 1} has no witness.`)
    }
    const witness = witnessById.get(witnessId)
    if (!witness) {
      throw new Error(`Agent edit ${operationIndex + 1} targets an unavailable witness.`)
    }
    changedWitnessIds.add(witnessId)
    // The edit contract's target rows define the recovery boundary. Some
    // reconciliation paths preserve a unit identity while changing its text,
    // so their narrower changed-row report is not sufficient for atomic undo.
    operationRowIds(operation).forEach(rowId => changedRowIds.add(rowId))

    if (['boundary_shift', 'split_unit', 'merge_units'].includes(operation.kind)) {
      if (!witness.segmentation_result || !witness.uploaded_file?.content) {
        throw new Error(`Agent edit ${operationIndex + 1} requires source-backed witness segmentation.`)
      }
      const previousRows = rows
      const previousSegmentationResult = witness.segmentation_result
      let nextSegmentationResult: NonNullable<Witness['segmentation_result']>
      if (operation.kind === 'boundary_shift') {
        nextSegmentationResult = applyLedgerSafeBoundaryShiftToSegmentation({
          rows,
          witnessId,
          leftRowId: String(operation.payload.leftRowId || ''),
          segmentationResult: previousSegmentationResult,
          sourceText: witness.uploaded_file.content,
          deltaWords: Number(operation.payload.deltaWords || 0),
        }).updatedSegmentationResult
      } else if (operation.kind === 'split_unit') {
        nextSegmentationResult = applyLedgerSafeUnitSplitToSegmentation({
          rows,
          witnessId,
          rowId: String(operation.payload.rowId || ''),
          segmentationResult: previousSegmentationResult,
          sourceText: witness.uploaded_file.content,
          splitBoundaryWordIndex: Number(operation.payload.afterWordIndex || 0),
        }).updatedSegmentationResult
      } else {
        nextSegmentationResult = applyLedgerSafeMergeUnitsToSegmentation({
          rows,
          witnessId,
          rowIds: operationRowIds(operation),
          segmentationResult: previousSegmentationResult,
          sourceText: witness.uploaded_file.content,
        }).updatedSegmentationResult
      }
      const reconcile = reconcileLedgerSafeBoundaryEdit({
        witnessId,
        previousSegmentationResult,
        nextSegmentationResult,
        rows,
      })
      rows = reconcile.updatedRows
      reconcile.changedRowIds.forEach(rowId => changedRowIds.add(rowId))
      witnessById.set(witnessId, updateWitnessSegmentation(witness, nextSegmentationResult, timestamp))
      if (characterLayer) {
        characterLayer = rebaseCharacterLayerAfterSegmentationEdit({
          layer: characterLayer,
          witnessId,
          previousSegmentationResult,
          nextSegmentationResult,
          previousRows,
          nextRows: rows,
        })
      }
      return
    }

    if (operation.kind === 'shift_range') {
      const result = applyLedgerSafeShiftRange({
        rows,
        witnessId,
        rowIds: operationRowIds(operation),
        direction: operation.payload.direction === 'up' ? 'up' : 'down',
      })
      rows = result.updatedRows
      result.changedRowIds.forEach(rowId => changedRowIds.add(rowId))
      return
    }

    if (operation.kind === 'mark_gap' || operation.kind === 'clear_gap') {
      operationRowIds(operation).forEach(rowId => {
        const result = operation.kind === 'mark_gap'
          ? applyLedgerSafeMarkGap({ rows, witnessId, rowId })
          : applyLedgerSafeClearGap({ rows, witnessId, rowId })
        rows = result.updatedRows
        result.changedRowIds.forEach(changedRowId => changedRowIds.add(changedRowId))
      })
      return
    }

    throw new Error(`Unsupported agent edit operation ${operation.kind}.`)
  })

  const affectedRowIds = unique(Array.from(changedRowIds))
  const affectedWitnessIds = unique(Array.from(changedWitnessIds))
  const witnesses = input.witnesses.map(witness => witnessById.get(witness.id) || witness)
  const indices = input.includeCharacterIndex && characterLayer
    ? [buildPiMatrixAgentCharacterIndex(characterLayer)]
    : []
  const contentHash = buildPiMatrixAgentSnapshot({
    chapterId: input.record.chapterId,
    chapterLabel: input.chapterLabel,
    rows,
    witnesses,
    indices,
  }).contentHash
  const appliedAt = input.approvedAt || nowIso()
  const beforeAuthority: LedgerSafeAuthorityHeadRecord = {
    chapterId: input.record.chapterId,
    schemaVersion: 1,
    bookId: input.record.bookId,
    authorityRevisionId: input.record.baseAuthorityRevisionId,
    contentHash: input.record.baseContentHash,
    updatedAt: input.record.persistedAt,
  }
  const afterAuthority: LedgerSafeAuthorityHeadRecord = {
    chapterId: input.record.chapterId,
    schemaVersion: 1,
    bookId: input.record.bookId,
    authorityRevisionId: newAuthorityRevisionId(),
    contentHash,
    updatedAt: appliedAt,
    sourceChangeSetId: input.record.id,
  }
  const before: LedgerSafeAgentRecoveryState = {
    rows: selectRows(originalRows, affectedRowIds),
    witnesses: selectWitnesses(originalWitnesses, affectedWitnessIds),
    ...(originalCharacterLayer ? { characterLayer: originalCharacterLayer } : {}),
  }
  const after: LedgerSafeAgentRecoveryState = {
    rows: selectRows(rows, affectedRowIds),
    witnesses: selectWitnesses(witnesses, affectedWitnessIds),
    ...(persistCharacterLayer && characterLayer ? { characterLayer: clone(characterLayer) } : {}),
  }
  const actor: LedgerSafeLlmContributor = {
    kind: 'llm',
    id: input.record.agentSessionId || `${input.model.provider || 'model'}:${input.model.modelId}`,
    displayName: input.model.displayName,
    provider: input.model.provider,
    modelId: input.model.modelId,
    thinkingLevel: input.model.thinkingLevel,
    role: 'assistant',
    contributedAt: appliedAt,
  }
  const editorialEvent = createLedgerSafeEditorialEvent({
    chapterId: input.record.chapterId,
    actor,
    source: 'pi-preview-apply',
    summary: `Applied ${input.patch.operations.length} approved Pi edit${input.patch.operations.length === 1 ? '' : 's'} as one sequence`,
    operation: {
      kind: 'agent_change_set',
      params: { changeSetId: input.record.id, operationCount: input.patch.operations.length },
    },
    affectedWitnessIds,
    affectedRowIds,
    beforeRows: buildLedgerSafeEditorialRowSnapshots(originalRows, affectedRowIds),
    afterRows: buildLedgerSafeEditorialRowSnapshots(rows, affectedRowIds),
    uiContext: input.uiContext,
    metadata: {
      patchId: input.patch.patchId,
      baseAuthorityRevisionId: input.record.baseAuthorityRevisionId,
      authorityRevisionId: afterAuthority.authorityRevisionId,
      stepSummaries: input.record.steps.map(step => step.summary),
    },
  })
  const nextChangeSet: LedgerSafeAgentChangeSetRecord = {
    ...clone(input.record),
    status: 'applied',
    approval: { required: true, status: 'approved', approvedAt: appliedAt },
    updatedAt: appliedAt,
    application: {
      appliedAt,
      before,
      after,
      beforeAuthority,
      afterAuthority,
      editorialEventId: editorialEvent.id,
    },
  }

  return {
    rows,
    witnesses,
    characterLayer: persistCharacterLayer ? characterLayer : undefined,
    mutation: {
      changeSetId: input.record.id,
      expectedChangeSetStatus: 'awaiting_approval',
      expectedAuthority: beforeAuthority,
      expectedState: before,
      nextState: after,
      nextChangeSet,
      nextAuthority: afterAuthority,
      editorialEvent,
    },
  }
}

export const commitPreparedLedgerSafeAgentApplication = async (
  application: PreparedLedgerSafeAgentApplication,
): Promise<PreparedLedgerSafeAgentApplication> => {
  await dbStorage.commitLedgerSafeAgentMutation(application.mutation)
  return application
}

export interface PreparedLedgerSafeAgentUndo {
  mutation: LedgerSafeAgentAtomicMutation
  rows: AnchoredRow[]
  witnesses: Witness[]
  characterLayer?: ChapterCharacterLayer
}

export const prepareLedgerSafeAgentUndo = (
  record: LedgerSafeAgentChangeSetRecord,
  uiContext: LedgerSafeEditorialEvent['uiContext'],
  undoneAt = nowIso(),
): PreparedLedgerSafeAgentUndo => {
  if (record.status !== 'applied' || !record.application) {
    throw new Error('Only an applied agent edit sequence can be undone.')
  }
  const application = record.application
  const timestamp = Date.parse(undoneAt) || Date.now()
  const restoredWitnesses = application.before.witnesses.map((witness, index) => ({
    ...clone(witness),
    last_modified: timestamp,
    version: (application.after.witnesses[index]?.version || witness.version || 0) + 1,
  }))
  const restoredRows = application.before.rows.map(row => ({ ...clone(row), updatedAt: undoneAt }))
  const restoredCharacterLayer = application.before.characterLayer
    ? {
        ...clone(application.before.characterLayer),
        version: (application.after.characterLayer?.version || application.before.characterLayer.version) + 1,
        updated_at: undoneAt,
      }
    : undefined
  const nextState: LedgerSafeAgentRecoveryState = {
    rows: restoredRows,
    witnesses: restoredWitnesses,
    ...(restoredCharacterLayer ? { characterLayer: restoredCharacterLayer } : {}),
  }
  const undoAuthority: LedgerSafeAuthorityHeadRecord = {
    ...application.beforeAuthority,
    authorityRevisionId: newAuthorityRevisionId(),
    updatedAt: undoneAt,
    sourceChangeSetId: record.id,
  }
  const editorialEvent = createLedgerSafeEditorialEvent({
    chapterId: record.chapterId,
    source: 'pi-preview-apply',
    summary: `Undid approved Pi edit sequence (${record.steps.length} edit${record.steps.length === 1 ? '' : 's'})`,
    operation: {
      kind: 'undo_agent_change_set',
      params: { changeSetId: record.id, operationCount: record.steps.length },
    },
    affectedWitnessIds: record.affectedWitnessIds,
    affectedRowIds: record.affectedRowIds,
    beforeRows: buildLedgerSafeEditorialRowSnapshots(application.after.rows, record.affectedRowIds),
    afterRows: buildLedgerSafeEditorialRowSnapshots(restoredRows, record.affectedRowIds),
    uiContext,
    metadata: {
      appliedEditorialEventId: application.editorialEventId,
      restoredContentHash: application.beforeAuthority.contentHash,
      authorityRevisionId: undoAuthority.authorityRevisionId,
    },
  })
  const nextChangeSet: LedgerSafeAgentChangeSetRecord = {
    ...clone(record),
    status: 'undone',
    updatedAt: undoneAt,
    application: {
      ...clone(application),
      undoneAt,
      undoAuthority,
      undoEditorialEventId: editorialEvent.id,
    },
  }
  return {
    rows: restoredRows,
    witnesses: restoredWitnesses,
    characterLayer: restoredCharacterLayer,
    mutation: {
      changeSetId: record.id,
      expectedChangeSetStatus: 'applied',
      expectedAuthority: application.afterAuthority,
      expectedState: application.after,
      nextState,
      nextChangeSet,
      nextAuthority: undoAuthority,
      editorialEvent,
    },
  }
}

export const commitPreparedLedgerSafeAgentUndo = async (
  undo: PreparedLedgerSafeAgentUndo,
): Promise<PreparedLedgerSafeAgentUndo> => {
  await dbStorage.commitLedgerSafeAgentMutation(undo.mutation)
  return undo
}
